/**
 * @file Reset Password Page — /reset-password?token=...
 */
import React, { useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { authService } from "../services/auth";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input } from "../ui";

const schema = Yup.object({
  password: Yup.string().min(10, S.PASSWORD_MIN).required(S.REQUIRED_FIELD),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], S.PASSWORDS_DONT_MATCH)
    .required(S.REQUIRED_FIELD),
});

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <AuthShell title={S.RESET_TITLE}>
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>Token de restablecimiento no proporcionado.</span>
        </div>
        <Button asChild fullWidth size="lg">
          <RouterLink to="/login">Volver al inicio de sesión</RouterLink>
        </Button>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell title={S.RESET_TITLE}>
        <div className="auth-alert auth-alert-ok">
          <CheckCircle2 size={14} />
          <span>{S.RESET_SUCCESS}</span>
        </div>
        <Button asChild fullWidth size="lg">
          <RouterLink to="/login">{S.LOGIN_SUBMIT}</RouterLink>
        </Button>
      </AuthShell>
    );
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");
      await authService.resetPassword(token, values.password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || "Error al restablecer la contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={S.RESET_TITLE} subtitle={S.RESET_SUBTITLE}>
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form className="auth-form">
            <Input
              name="password"
              type="password"
              label={S.RESET_PASSWORD}
              autoComplete="new-password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && errors.password}
            />
            <Input
              name="confirmPassword"
              type="password"
              label={S.RESET_CONFIRM}
              autoComplete="new-password"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPassword && errors.confirmPassword}
            />
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {S.RESET_SUBMIT}
            </Button>
          </Form>
        )}
      </Formik>
    </AuthShell>
  );
}
